import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { IntegrationMDMProxyService, RemoveProfileCommand } from '@rocksoft/locations-sdk';
import { ConfigurationProfile, ConfigurationProfileInstallProfileCommand } from '@rocksoft/mdm-sdk-v2';
import { map, mergeMap } from 'rxjs/operators';
import { safeMap } from '../effects.helpers';
import {
  AppleCurrentDeviceContainerActions,
  AppleDeviceContainerActions,
  AppleDeviceResolverActions,
  MdmAppleDevicesApiActions,
  MdmAppleProfileEffectsActions,
} from './apple-profile.actions';

@Injectable()
export class AppleProfileEffects {
  private readonly mdmCommandProxyService = inject(IntegrationMDMProxyService);
  private readonly actions$ = inject(Actions);

  private readonly removeSingleAppModeProfile: RemoveProfileCommand = {
    requestRequiresNetworkTether: false,
    identifier: 'com.methylium.applock',
  };

  appleProfileLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppleDeviceResolverActions.profilesRequested, AppleDeviceContainerActions.profilesRequested),
      mergeMap(({ udid }) =>
        safeMap(
          this.mdmCommandProxyService.getConfigurationProfiles({ udid }),
          (profiles) =>
            MdmAppleDevicesApiActions.profilesLoaded({
              udid: udid,
              profiles: profiles as unknown as ConfigurationProfile[], // Cast is needed because the ERA `ConfigurationProfile.payloadIdentifier` is optional
            }),
          (error) => MdmAppleDevicesApiActions.profilesLoadedError({ error })
        )
      )
    );
  });

  singleAppModeEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppleDeviceContainerActions.singleAppModeChanged, AppleCurrentDeviceContainerActions.singleAppModeChanged),
      mergeMap(({ udid, bundleIdentifier, locked }) => {
        const addCommand = this.singleAppModeProfile(udid, bundleIdentifier);
        const removeCommand = this.removeSingleAppModeProfile;

        const add$ = this.mdmCommandProxyService.queueInstallAppLockProfileCommand({ udid, bundleIdentifier }).pipe(
          map(() =>
            MdmAppleProfileEffectsActions.profileAdded({
              udid: udid,
              profile: addCommand.payload,
            })
          )
        );

        const remove$ = this.mdmCommandProxyService
          .queueRemoveProfileCommand({ udid, removeProfileCommand: removeCommand })
          .pipe(
            map(() =>
              MdmAppleProfileEffectsActions.profileRemoved({
                udid: udid,
                profileIdentifier: removeCommand.identifier ?? '',
              })
            )
          );

        return locked ? add$ : remove$;
      })
    );
  });

  // Used as an temp profile until a refresh off all installed profiles for the device is done.
  private singleAppModeProfile(
    udid: string,
    bundleIdentifier: string
  ): ConfigurationProfileInstallProfileCommand & { payload: ConfigurationProfile } {
    return {
      requestRequiresNetworkTether: false,
      payload: {
        udid,
        payloadIdentifier: 'com.methylium.applock',
        payloadDisplayName: `App Lock for: ${bundleIdentifier}`,
        payloadUUID: '', // Will be set in the API
      },
    };
  }
}
